import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import AuthService from '../../../utilities/services/auth.service'

import {
  getPayoutTransactions,
  listPayoutReportTemplates,
  savePayoutReportTemplate,
  deletePayoutReportTemplate,
} from '../../../utilities/api'

import {
  formatCurrency,
  formatString,
  createWorkbook,
  saveWorkbook,
  currencyFormat,
  numberFormat,
  dateFormat,
  capitalizeString,
  setHeaders,
  setColumnsAndRows,
  DEFAULTNUMHEADERS,
  addWorksheet,
} from '../../../utilities/helpers'
import { isHensonFundReportEnabled } from '@blocktickets/shared-utils'

import Stack from 'react-bootstrap/Stack'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'

import { BackButton } from '../../BackButton'
import { Transaction } from './Transaction'
import { LoadingContainer } from '../../LoadingContainer'
import {
  AUX_ADMIN_TEMPLATE_VALUE,
  DEFAULT_TEMPLATE_VALUE,
  applyBuiltInTemplate,
  buildEventSummaries,
  formatPayoutTransactionRow,
  getBuiltInTemplateOptions,
  mergeSavedColumnsWithDefaults,
  pickVisibleRowValues,
  serializeColumnsForTemplate,
  sumPayoutNetSales,
} from './payoutReportTemplates'
import '../../PaymentListingsV4Wrapper/paymentListingsV4.scss'

const DragHandleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="12" r="1" />
    <circle cx="9" cy="5" r="1" />
    <circle cx="9" cy="19" r="1" />
    <circle cx="15" cy="12" r="1" />
    <circle cx="15" cy="5" r="1" />
    <circle cx="15" cy="19" r="1" />
  </svg>
)

const SettingsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const ResetIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
)

const SortableColumnItem = ({ col, index, toggleColumn }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`column-settings-item ${isDragging ? 'is-dragging' : ''}`}
    >
      <Form.Check
        type="switch"
        id={`payout-col-${col.id}`}
        checked={col.visible}
        onChange={() => toggleColumn(index)}
      />
      <div className="column-label">{col.label}</div>
      <div {...attributes} {...listeners} className="drag-handle">
        <DragHandleIcon />
      </div>
    </div>
  )
}

export {
  serializeColumnsForTemplate,
  mergeSavedColumnsWithDefaults,
  buildEventSummaries,
  formatPayoutTransactionRow,
  applyBuiltInTemplate,
} from './payoutReportTemplates'

export default function ViewPayoutWrapper({ payout, accountId }) {
  const organization = AuthService.getOrg()[0]
  const organizationUUID = organization?.uuid
  const includeHensonFee = isHensonFundReportEnabled(organizationUUID)

  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('payouts')
  const [transactions, setTransactions] = useState([])
  const [refundsTotalCents, setRefundsTotalCents] = useState(0)

  const initialTemplate = applyBuiltInTemplate(
    DEFAULT_TEMPLATE_VALUE,
    organizationUUID
  )
  const [columns, setColumns] = useState(initialTemplate.columns)
  const [includeEventSummary, setIncludeEventSummary] = useState(
    initialTemplate.includeEventSummary
  )
  const [savedTemplates, setSavedTemplates] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    DEFAULT_TEMPLATE_VALUE
  )
  const [templateName, setTemplateName] = useState('')
  const [showSettings, setShowSettings] = useState(false)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const [templateError, setTemplateError] = useState('')
  const [templateMessage, setTemplateMessage] = useState('')

  const payoutTransactions = transactions || []
  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns]
  )
  const selectedSavedTemplate = useMemo(
    () =>
      savedTemplates.find(
        (template) => String(template.id) === String(selectedTemplateId)
      ),
    [savedTemplates, selectedTemplateId]
  )

  const netTotal = sumPayoutNetSales(payoutTransactions)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (payout) {
      setIsLoading(true)
      loadTransactions()
    }
  }, [payout, accountId])

  useEffect(() => {
    if (!organizationUUID) return
    listPayoutReportTemplates(organizationUUID)
      .then((res) => {
        const templates = Array.isArray(res?.data?.templates)
          ? res.data.templates
          : []
        setSavedTemplates(templates)
      })
      .catch((err) => {
        console.error('Error loading payout report templates:', err)
      })
  }, [organizationUUID])

  const loadTransactions = () => {
    getPayoutTransactions(accountId, payout.id)
      .then((res) => {
        setTransactions(res?.data?.orders || [])
        setRefundsTotalCents(res?.data?.refundsTotal || 0)
        setIsLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setIsLoading(false)
      })
  }

  const resetToDefaultTemplate = () => {
    const next = applyBuiltInTemplate(DEFAULT_TEMPLATE_VALUE, organizationUUID)
    setSelectedTemplateId(DEFAULT_TEMPLATE_VALUE)
    setColumns(next.columns)
    setIncludeEventSummary(next.includeEventSummary)
    setTemplateName('')
    setTemplateError('')
    setTemplateMessage('')
  }

  const applyTemplate = (templateId) => {
    if (
      !templateId ||
      templateId === DEFAULT_TEMPLATE_VALUE ||
      templateId === AUX_ADMIN_TEMPLATE_VALUE
    ) {
      const next = applyBuiltInTemplate(
        templateId || DEFAULT_TEMPLATE_VALUE,
        organizationUUID
      )
      setSelectedTemplateId(next.id)
      setColumns(next.columns)
      setIncludeEventSummary(next.includeEventSummary)
      setTemplateName(
        next.builtIn && next.id === AUX_ADMIN_TEMPLATE_VALUE ? next.name : ''
      )
      setTemplateError('')
      setTemplateMessage('')
      return
    }

    const template = savedTemplates.find(
      (item) => String(item.id) === String(templateId)
    )
    if (!template) {
      resetToDefaultTemplate()
      return
    }

    setSelectedTemplateId(template.id)
    setTemplateName(template.name || '')
    setColumns(
      mergeSavedColumnsWithDefaults(template.columns || [], organizationUUID)
    )
    setIncludeEventSummary(Boolean(template.includeEventSummary))
    setTemplateError('')
    setTemplateMessage('')
  }

  const toggleColumn = (index) => {
    setColumns((prev) =>
      prev.map((col, idx) =>
        idx === index ? { ...col, visible: !col.visible } : col
      )
    )
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setColumns((prev) => {
      const oldIndex = prev.findIndex((col) => col.id === active.id)
      const newIndex = prev.findIndex((col) => col.id === over.id)
      if (oldIndex < 0 || newIndex < 0) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const handleSaveTemplate = async ({ updateExisting = false } = {}) => {
    const name = templateName.trim()
    if (!name) {
      setTemplateError('Enter a name for this saved report.')
      return
    }
    if (!organizationUUID) {
      setTemplateError('Organization is required to save this report.')
      return
    }

    setIsSavingTemplate(true)
    setTemplateError('')
    setTemplateMessage('')

    try {
      const res = await savePayoutReportTemplate({
        id:
          updateExisting && selectedSavedTemplate
            ? selectedSavedTemplate.id
            : undefined,
        organizationUUID,
        name,
        columns: serializeColumnsForTemplate(columns),
        includeEventSummary,
      })
      const saved = res?.data
      const listRes = await listPayoutReportTemplates(organizationUUID)
      const templates = Array.isArray(listRes?.data?.templates)
        ? listRes.data.templates
        : []
      setSavedTemplates(templates)
      const active =
        templates.find(
          (template) => String(template.id) === String(saved?.id)
        ) || saved
      if (active?.id) {
        setSelectedTemplateId(active.id)
        setTemplateName(active.name || name)
        setIncludeEventSummary(Boolean(active.includeEventSummary))
        setColumns(
          mergeSavedColumnsWithDefaults(active.columns || [], organizationUUID)
        )
      }
      setTemplateMessage('Report saved.')
    } catch (error) {
      console.error('Error saving payout report template:', error)
      setTemplateError(
        error?.response?.data?.error?.message ||
          'Could not save this report. Please try again.'
      )
    } finally {
      setIsSavingTemplate(false)
    }
  }

  const handleDeleteTemplate = async () => {
    if (!selectedSavedTemplate || !organizationUUID) return

    setIsSavingTemplate(true)
    setTemplateError('')
    setTemplateMessage('')

    try {
      await deletePayoutReportTemplate(
        selectedSavedTemplate.id,
        organizationUUID
      )
      setSavedTemplates((prev) =>
        prev.filter(
          (template) => String(template.id) !== String(selectedSavedTemplate.id)
        )
      )
      resetToDefaultTemplate()
      setTemplateMessage('Saved report deleted.')
    } catch (error) {
      console.error('Error deleting payout report template:', error)
      setTemplateError(
        error?.response?.data?.error?.message ||
          'Could not delete this report. Please try again.'
      )
    } finally {
      setIsSavingTemplate(false)
    }
  }

  const appendEventSummarySection = (worksheet, reportData) => {
    const summary = buildEventSummaries(reportData, { includeHensonFee })
    worksheet.addRow([])

    const headers = includeHensonFee
      ? ['Event', 'Facility Fee', 'Henson Fee', 'Sales', 'Net Payout']
      : ['Event', 'Facility Fee', 'Sales', 'Net Payout']
    const headerRow = worksheet.addRow(headers)
    headerRow.font = { bold: true }

    const writeSummaryRow = (row) => {
      const values = includeHensonFee
        ? [row.label, row.facilityFee, row.hensonFee, row.sales, row.netPayout]
        : [row.label, row.facilityFee, row.sales, row.netPayout]
      return worksheet.addRow(values)
    }

    for (const row of summary.rows) {
      writeSummaryRow(row)
    }

    const totalRow = writeSummaryRow(summary.total)
    totalRow.font = { bold: true }

    const summaryStartRow = headerRow.number + 1
    const summaryEndRow = totalRow.number
    const currencyCols = includeHensonFee ? [2, 3, 4, 5] : [2, 3, 4]
    for (const colNum of currencyCols) {
      for (let rowNum = summaryStartRow; rowNum <= summaryEndRow; rowNum += 1) {
        const cell = worksheet.getRow(rowNum).getCell(colNum)
        cell.numFmt = '"$"#,##0.00;[Red]-"$"#,##0.00'
      }
    }

    worksheet.addRow([])
    const footnoteRow = worksheet.addRow([
      'Unmatched Totals are payout amounts that could not be tied to an event, package, or pass.',
    ])
    footnoteRow.font = { italic: true, size: 10 }
  }

  const handleExcelExport = async () => {
    try {
      if (!payoutTransactions?.length) return

      const completed = moment.unix(payout?.arrival_date).format('MMM Do YYYY')
      const workbook = createWorkbook()
      const worksheet = addWorksheet(`Payout - ${completed}`, workbook)
      const columnsForExport =
        visibleColumns.length > 0
          ? visibleColumns
          : columns.filter((col) => col.id === 'net_sales')

      const format = (transaction) =>
        pickVisibleRowValues(
          formatPayoutTransactionRow(transaction),
          columnsForExport
        )

      const headers = [
        `Total Amount: ${formatCurrency(payout?.amount / 100)}`,
        `Completed on: ${completed}`,
        `Status: ${capitalizeString(payout?.status)}`,
      ]

      setHeaders('Payout', organization?.name, worksheet, headers)

      setColumnsAndRows(
        columnsForExport.map((col) => col.id),
        payoutTransactions,
        format,
        worksheet
      )

      const headerRows = DEFAULTNUMHEADERS + headers.length
      const columnHeaderRow = worksheet.getRow(headerRows)
      columnsForExport.forEach((col, index) => {
        columnHeaderRow.getCell(index + 1).value = col.label
      })
      columnHeaderRow.commit?.()

      columnsForExport.forEach((col, index) => {
        const colNum = index + 1
        if (
          [
            'base_price',
            'service_fee',
            'facility_fee',
            'henson_fee',
            'sales_tax',
            'chargeBack_protection',
            'processing_fee',
            'gross_sales',
            'net_sales',
            'amount_remaining',
          ].includes(col.id)
        ) {
          currencyFormat(colNum, worksheet, headerRows)
        } else if (['installments', 'quantity'].includes(col.id)) {
          numberFormat(colNum, worksheet, headerRows)
        } else if (['event_date', 'order_date'].includes(col.id)) {
          dateFormat(colNum, worksheet, headerRows)
        }
      })

      if (includeEventSummary) {
        appendEventSummarySection(worksheet, payoutTransactions)
      }

      await saveWorkbook(workbook, 'payout')
    } catch (err) {
      console.error(err)
      alert(err.message)
    }
  }

  return (
    <>
      {isLoading ? (
        <LoadingContainer />
      ) : (
        <Card body>
          <Stack
            direction="horizontal"
            gap={3}
            className="justify-content-between flex-wrap"
          >
            <Stack direction="horizontal" gap={4} className="align-items-start">
              <BackButton size="sm" />
              <Stack gap={2}>
                <Card.Title as="h5" className="small small-bold">
                  Payout
                </Card.Title>
                <Stack direction="horizontal" gap={3}>
                  <span className="fs-md fs-md-bold">
                    {formatCurrency(payout?.amount / 100)}
                  </span>
                  <Badge bg="none" className={`badge--${payout?.status}`}>
                    {formatString(payout?.status)}
                  </Badge>
                  <span className="subtitle subtitle-bolder m-0">
                    Refunds {formatCurrency(refundsTotalCents / 100)}
                  </span>
                </Stack>
                {payout?.arrival_date && (
                  <span className="subtitle subtitle-bolder m-0">
                    Completed on{' '}
                    {moment.unix(payout?.arrival_date).format('MMM Do YYYY')}
                  </span>
                )}
              </Stack>
            </Stack>
            <Stack direction="horizontal" gap={2} className="flex-nowrap">
              <Form.Group controlId="payoutSavedReportSelect">
                <Form.Label className="uppercase text-body-xs font-weight-bold mb-1">
                  Saved Report
                </Form.Label>
                <Form.Select
                  className="form-control-height"
                  value={selectedTemplateId}
                  onChange={(e) => applyTemplate(e.target.value)}
                  disabled={isSavingTemplate}
                  style={{ minWidth: '200px' }}
                >
                  {getBuiltInTemplateOptions(organizationUUID).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                  {savedTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button
                variant="outline-light"
                className="icon-button form-control-height d-flex align-items-center gap-2 px-4 align-self-end"
                onClick={() => {
                  setTemplateError('')
                  setTemplateMessage('')
                  if (selectedSavedTemplate) {
                    setTemplateName(selectedSavedTemplate.name || '')
                  } else if (selectedTemplateId === AUX_ADMIN_TEMPLATE_VALUE) {
                    setTemplateName('Aux Admin')
                  }
                  setShowSettings(true)
                }}
              >
                <SettingsIcon />
                <span>Edit columns</span>
              </Button>
              <Button
                variant="outline-light"
                className="icon-button btn-download form-control-height align-self-end"
                disabled={!payoutTransactions?.length}
                onClick={handleExcelExport}
              >
                Export Payouts
              </Button>
            </Stack>
          </Stack>
          <div className="seperator seperator-sm">
            <Tabs
              id="payout-report-tabs"
              activeKey={activeTab}
              onSelect={(key) => setActiveTab(key || 'payouts')}
              variant="pills"
              className="w-max-content nav-pills-default mb-3"
            >
              <Tab eventKey="payouts" title="Payouts">
                <h2 className="normal mb-3">Transactions</h2>
                <div className="table-container">
                  <div className="table-container--fixed-height--scrollable">
                    <Table responsive>
                      <thead>
                        <tr className="md md--scroll">
                          <th className="text-left uppercase text-body">
                            Order id
                          </th>
                          <th className="text-left uppercase text-body">
                            Order date
                          </th>
                          <th className="text-left uppercase text-body">
                            Net sales
                          </th>
                          <th className="uppercase text-body">
                            Payment method
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {payoutTransactions?.map((transaction, idx) => (
                          <Transaction key={idx} transaction={transaction} />
                        ))}
                        {payoutTransactions?.length === 0 && (
                          <tr className="md-x md-x--scroll">
                            <td colSpan={4} className="text-center">
                              No payout orders found.
                            </td>
                          </tr>
                        )}
                        {payoutTransactions?.length > 0 && (
                          <tr className="md-x md-x--scroll">
                            <td className="text-right" colSpan={2}>
                              Totals
                            </td>
                            <td className="text-left">
                              {formatCurrency(netTotal)}
                            </td>
                            <td></td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Tab>
            </Tabs>
          </div>
        </Card>
      )}

      <Modal
        show={showSettings}
        onHide={() => setShowSettings(false)}
        size="lg"
        scrollable
        className="column-settings-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Column Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="subtitle">
            Toggle columns on or off and rearrange their order in the export.
            Optionally include event reconciliation totals for Aux Admin-style
            reports.
          </p>
          <div className="column-template-save mb-4">
            <Form.Group controlId="payoutSavedReportName">
              <Form.Label className="uppercase text-body-xs font-weight-bold">
                Report name
              </Form.Label>
              <Form.Control
                type="text"
                className="form-control-height"
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value)
                  if (templateError) setTemplateError('')
                  if (templateMessage) setTemplateMessage('')
                }}
                placeholder="e.g. Aux Admin"
                maxLength={60}
                disabled={isSavingTemplate}
              />
            </Form.Group>
            <Form.Check
              className="mt-3"
              type="switch"
              id="payout-include-event-summary"
              label="Include event reconciliation totals"
              checked={includeEventSummary}
              onChange={(e) => setIncludeEventSummary(e.target.checked)}
              disabled={isSavingTemplate}
            />
            <p className="text-muted small mt-2 mb-0">
              Saves the current column layout
              {includeHensonFee
                ? ' (including Henson Fee when enabled)'
                : ''}{' '}
              and whether event totals are included in the export.
            </p>
            {templateError ? (
              <p className="text-danger small mt-2 mb-0">{templateError}</p>
            ) : null}
            {templateMessage ? (
              <p className="text-success small mt-2 mb-0">{templateMessage}</p>
            ) : null}
            <Stack direction="horizontal" gap={2} className="mt-3 flex-wrap">
              <Button
                variant="outline-light"
                className="btn-pill btn-cancel"
                disabled={isSavingTemplate}
                onClick={() => handleSaveTemplate({ updateExisting: false })}
              >
                {isSavingTemplate ? 'Saving…' : 'Save as new report'}
              </Button>
              {selectedSavedTemplate && (
                <>
                  <Button
                    variant="primary"
                    className="btn-pill btn-save"
                    disabled={isSavingTemplate}
                    onClick={() => handleSaveTemplate({ updateExisting: true })}
                  >
                    Update report
                  </Button>
                  <Button
                    variant="outline-danger"
                    className="btn-pill"
                    disabled={isSavingTemplate}
                    onClick={handleDeleteTemplate}
                  >
                    Delete report
                  </Button>
                </>
              )}
            </Stack>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={columns.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap={0}>
                {columns.map((col, index) => (
                  <SortableColumnItem
                    key={col.id}
                    col={col}
                    index={index}
                    toggleColumn={toggleColumn}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-light"
            className="btn-pill btn-reset"
            onClick={() => {
              const next = applyBuiltInTemplate(
                selectedTemplateId === AUX_ADMIN_TEMPLATE_VALUE
                  ? AUX_ADMIN_TEMPLATE_VALUE
                  : DEFAULT_TEMPLATE_VALUE,
                organizationUUID
              )
              setColumns(next.columns)
              setIncludeEventSummary(next.includeEventSummary)
            }}
            disabled={isSavingTemplate}
          >
            <ResetIcon />
            Reset to default
          </Button>
          <Stack direction="horizontal" gap={2}>
            <Button
              variant="outline-light"
              className="btn-pill btn-cancel"
              onClick={() => setShowSettings(false)}
              disabled={isSavingTemplate}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-pill btn-save"
              onClick={() => setShowSettings(false)}
              disabled={isSavingTemplate}
            >
              Done
            </Button>
          </Stack>
        </Modal.Footer>
      </Modal>
    </>
  )
}
